import Spinner from "@/Components/Spinner/Spinner";
import { selectedGarnishAtom } from "@/shared/atoms";
import { useModalMeta } from "@/shared/atoms/useModal";
import useCategoryHook from "@/shared/hooks/useCategoryHook";
import { useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { arrayMove } from "react-sortable-hoc";
import {
  getLabel,
  toppingContentTypeOptions,
} from "../../shared/utils/helpers";
import { Garnish, Topping } from "../../shared/utils/interfaces";
import Checkbox from "../Checkbox/Checkbox";
import Icon from "../Icon/Icon";
import IconButton from "../IconButton/IconButton";
import TextfieldIntControlled from "../Input/TextfieldIntControlled";
import Select from "../Select/Select";
import SortableSelect from "../Select/SortableSelect";

interface GarnishListProps {
  garnish: Partial<Garnish>;
  setCreate?: (data: boolean) => void;
  setTabIndex?: (index: number) => void;
  toppings: Topping[];
  refetch?: () => void;
  isFetching?: boolean;
}

const GarnishList = ({
  garnish,
  setCreate,
  setTabIndex,
  toppings,
  refetch,
  isFetching,
}: GarnishListProps) => {
  const { t } = useTranslation();
  const [{ product: isEdit }] = useModalMeta();
  const methods = useForm();
  const { handleSubmit, control, watch, setValue } = methods;
  const [edit, setEdit] = useState<boolean>(setCreate ? true : false);
  const WrapperTag = edit ? "form" : "div";
  const setSelectedGarnish = useSetAtom(selectedGarnishAtom);
  const { garnishUpdate, garnishRemove, garnishCreate } = useCategoryHook();

  useEffect(() => {
    setValue("toppings", garnish.sub_content);
  }, [garnish.sub_content]);

  const onSubmit = async (data) => {
    let req = {
      ...garnish,
      ...data,
      toppings: (data.toppings || []).map((topping) => topping.item_id),
    };

    if (setCreate) {
      garnishCreate.mutateAsync(req, {
        onSuccess: () => {
          setCreate(false);
          refetch();
        },
      });
    } else {
      garnishUpdate.mutateAsync(req, {
        onSuccess: () => {
          setEdit(false);
          refetch();
        },
      });
    }
  };

  return (
    <FormProvider {...methods}>
      <WrapperTag
        onSubmit={handleSubmit(onSubmit)}
        className="w-full min-w-0 py-4"
      >
        <div className="flex min-w-0 flex-col gap-4 rounded-xl border border-border p-4">
          <div className="flex items-center justify-between gap-4 border-b border-border pb-3">
            <Controller
              defaultValue={garnish.is_active ?? false}
              name="is_active"
              control={control}
              render={(props) => (
                <div className="flex shrink-0 items-center gap-4">
                  <Checkbox
                    active={props.value}
                    onClick={() => props.onChange(!props.value)}
                    disabled={edit === false}
                  />
                  <span className="font-semibold">{t("menu.isActive")}</span>
                </div>
              )}
            />

            {edit ? (
              garnishUpdate.isPending ||
              garnishRemove.isPending ||
              garnishCreate.isPending ? (
                <Spinner></Spinner>
              ) : (
                <div className="flex items-center">
                  <IconButton
                    type="submit"
                    variants="check"
                    variant="success"
                    size="small"
                  />

                  {!setCreate && (
                    <div onClick={() => setEdit(false)} className="mx-2">
                      <IconButton
                        type="button"
                        variants="times"
                        variant="danger"
                        size="small"
                      />
                    </div>
                  )}

                  {!setCreate && (
                    <IconButton
                      onClick={() =>
                        garnishRemove.mutateAsync(garnish.item_id, {
                          onSuccess: () => {
                            setEdit(false);
                            refetch();
                          },
                        })
                      }
                      type="button"
                      variants="trash"
                      variant="danger"
                      size="small"
                    />
                  )}
                </div>
              )
            ) : (
              <div className="flex gap-8">
                <div onClick={() => setEdit(true)} className="cursor-pointer">
                  <Icon className="h-10 w-10 fill-primary" variants="edit" />
                </div>
                <div
                  onClick={() => {
                    setSelectedGarnish(garnish);
                    setTabIndex(Boolean(isEdit) !== false ? 2 : 1);
                  }}
                  className="cursor-pointer"
                >
                  <Icon className="h-10 w-10 fill-dark" variants="forward" />
                </div>
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4 xl:grid-cols-12">
            <div className="min-w-0 md:col-span-2 xl:col-span-5">
              <TextfieldIntControlled
                inputClassName="!bg-white"
                label={t("menu.garnishName")}
                placeholder={t("menu.extraCategoryNamePlaceholder")}
                required
                name="name"
                defaultValue={garnish?.name || {}}
                disabled={edit === false}
              />
            </div>

            <div className="min-w-0 xl:col-span-4">
              <TextfieldIntControlled
                inputClassName="!bg-white"
                label={t("garnishes.description")}
                placeholder={t("menu.extraCategoryDescriptionPlaceholder")}
                name="description"
                defaultValue={garnish?.description || {}}
                disabled={edit === false}
              />
            </div>

            <Controller
              defaultValue={garnish?.topping_content_type || 1}
              name="topping_content_type"
              control={control}
              render={(props) => (
                <div className="flex min-w-0 flex-col xl:col-span-3">
                  <div className="mb-4 min-w-0 font-semibold leading-snug xl:min-h-[2.75rem] xl:flex xl:items-center">
                    {t("garnishes.method")}
                  </div>
                  <div className="w-full min-w-0">
                    <Select
                      onChange={(v) => props.onChange(v.value)}
                      value={toppingContentTypeOptions.find(
                        (c) => c?.value === props.value,
                      )}
                      getOptionLabel={(option) =>
                        `${t(`menu.${option.label}`)}`
                      }
                      isDisabled={edit === false}
                      defaultValue={toppingContentTypeOptions.find(
                        (op) => +op.value === garnish?.topping_content_type,
                      )}
                      options={toppingContentTypeOptions}
                    />
                  </div>
                </div>
              )}
            />
          </div>

          <Controller
            defaultValue={garnish.sub_content}
            name="toppings"
            control={control}
            render={(props) => (
              <div className="w-full min-w-0">
                <SortableSelect
                  label={t("menu.toppings")}
                  onSortEnd={({ oldIndex, newIndex }) => {
                    const toppings = watch("toppings");
                    const newList = arrayMove(toppings, oldIndex, newIndex);
                    setValue("toppings", newList);
                  }}
                  styles={{
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    control: (base) => ({
                      ...base,
                      backgroundColor: "white",
                    }),
                  }}
                  menuPortalTarget={document.getElementById("root")}
                  onChange={(toppings) => props.onChange(toppings)}
                  placeholder={t("menu.toppings")}
                  isMulti
                  isSearchable
                  isDisabled={edit === false}
                  options={edit ? toppings : []}
                  value={props.value}
                  getOptionLabel={(option) => getLabel(option)}
                  getOptionValue={(option) => option.item_id}
                  isLoading={isFetching}
                />
              </div>
            )}
          />
        </div>
      </WrapperTag>
    </FormProvider>
  );
};

export default GarnishList;
